import { firebaseAuth } from './firebase.service';
import { User, onAuthStateChanged, signInWithCustomToken } from 'firebase/auth';
import { isProd } from '../environment';
import axios, { AxiosError } from 'axios';
import { JsonObject, UiOption } from '../helpers';
import { ApiResponse, handleResponse } from './index';
import { getUnixTime } from 'date-fns';
import { getUrlQueryParam, removeUrlQueryParam } from '../url-helpers';
import { post } from './sp-api.service';
import { debounce } from 'lodash';
import { FirebaseError } from 'firebase/app';

export type RootRole = 'root:owner' | 'root:admin' | 'root:auditor';

export type AuthRole = 'partner:owner' | 'partner:admin' | 'partner:auditor';

export type OrgRole = 'org:owner' | 'org:admin' | 'org:auditor';

export const OrgRoleOpts: UiOption[] = [
  { label: 'Owner', value: 'org:owner' },
  { label: 'Admin', value: 'org:admin' },
  { label: 'Auditor', value: 'org:auditor' },
];

export type AuthPermission = 'manage-admins' | 'read' | 'write' | 'super';

export type AuthUser = User & {
  token: string;
  partnerId: string;
  role: AuthRole;
  rootRole: RootRole | null;
};

class AuthProviderModel {
  user: AuthUser | null = null;

  get token(): string {
    return this.user?.token || '';
  }

  get partnerId(): string {
    return this.user?.partnerId || '';
  }
}

export const AuthProvider = new AuthProviderModel();

const axiosClient = axios.create({
  baseURL: '',
  timeout: 10000,
  withCredentials: false,
  responseType: 'json',
  headers: {
    'X-Requested-By': 'surepath',
  },
});

axiosClient.interceptors.request.use((config) => {
  if (AuthProvider.token) {
    config.headers['Surepath-Authorization'] = `Bearer ${AuthProvider.token}`;
  }

  return config;
});

const get = async (path: string, params?: JsonObject): Promise<ApiResponse> => {
  return axiosClient.get(path, { params }).then(handleResponse);
};

export const userCan = (user: AuthUser | null | undefined, perm: AuthPermission): boolean => {
  if (!user?.role && !user?.rootRole) {
    return false;
  }

  const { role, rootRole } = user;
  const isSuper = rootRole === 'root:owner';

  switch (perm) {
    case 'read':
      return isSuper || ['partner:owner', 'partner:admin', 'partner:auditor'].includes(role);
    case 'manage-admins':
      return ['partner:owner'].includes(role);
    case 'write':
      return isSuper || ['partner:owner', 'partner:admin'].includes(role);
    case 'super':
      return isSuper;
  }
};

const getBaseUrl = (subdomain: 'auth' | 'admin'): string => {
  let domain = `${subdomain}.surepath.ai`;

  if (!isProd()) {
    const [, environment] = window.location.hostname.split('.');

    if (['dev', 'stage', 'local'].includes(environment)) {
      domain = `${subdomain}.${environment}.surepath.ai`;
    }
  }

  return `https://${domain}`;
};

const getAuthBaseUrl = (): string => getBaseUrl('auth');

const getAdminBaseUrl = (): string => getBaseUrl('admin');

export const getAuthNoAccessUrl = (): string => {
  return `${getAuthBaseUrl()}/splash?action=partner-access`;
};

export const getAuthCallbackUrl = (): string => `${getAuthBaseUrl()}/__/auth/handler`;

export const getAuthRedirectUrl = (fromAuthSite?: boolean): string => {
  // user was just redirected here from auth? don't redirect them back
  const fas = typeof fromAuthSite === 'boolean' ? fromAuthSite : getFromAuthSite();
  if (fas) {
    return '/error?code=500';
  }

  const redirectDest = encodeURIComponent(window.location.origin);
  return `${getAuthBaseUrl()}/login?redirect_to=${redirectDest}`;
};

export const getFromAuthSite = (): boolean => {
  const fromAuthSite = getUrlQueryParam('fas');
  removeUrlQueryParam('fas');
  return Boolean(fromAuthSite);
};

export const signOut = async (redirect = true): Promise<boolean> => {
  try {
    await firebaseAuth.signOut();

    if (redirect) {
      window.location.href = `${getAuthBaseUrl()}/logout?redirect_to=${window.location.origin}`;
    }
    return true;
  } catch (err) {
    console.error(err);
    return false;
  }
};

export const getCurrentUser = async (): Promise<AuthUser | null> => {
  const fbAuthUser = await getCurrentFirebaseUser(true);

  if (fbAuthUser) {
    console.log('auth from ls');
    AuthProvider.user = fbAuthUser;
    return fbAuthUser;
  }

  const spAuthUser = await getCurrentTokenUser();

  if (spAuthUser) {
    console.log('auth from sp-token');
    AuthProvider.user = spAuthUser;
    return spAuthUser;
  }

  return null;
};

/*
 * Ensure that the JWT token is refreshed periodically so that other, non-firebase services can always
 * have an up-to-date token. Note that while Firebase will lazy-refresh the token for itself, other
 * services (like AtlasDataAPI) need a synchronous way of referencing an up-to-date token.
 *
 * While it seems as though you can listen for auth token expiration using onIdTokenChanged, this handler does not
 * actually fire when or before that happens:
 * https://github.com/firebase/firebase-js-sdk/issues/2985
 * https://github.com/firebase/firebase-js-sdk/blob/e9ff107eedbb9ec695ddc35e45bdd62734735674/packages/auth/src/core/index.ts#L136
 *
 */
export const observeTokenChange = () => {
  firebaseAuth.onIdTokenChanged((user: User) => {
    console.log('local auth change', user?.uid);
    if (!user) {
      AuthProvider.user = null;
      return;
    }

    getAuthUser(user).then((authUser) => {
      AuthProvider.user = authUser;
    });
  });

  const intMinutes = 1000 * 60 * 8;

  setInterval(() => {
    firebaseAuth.currentUser?.getIdTokenResult().then((result) => {
      const expTime = result?.claims?.exp;
      if (!expTime) {
        return;
      }

      const minuteDiff = (Number(expTime) - getUnixTime(new Date())) / 60;

      if (minuteDiff < 20) {
        firebaseAuth.currentUser?.getIdToken(true);
      }
    });
  }, intMinutes);
};

export const assumeRole = async (orgId: string, role: OrgRole): Promise<boolean> => {
  const response = await post('/auth/assume', { targetOrgId: orgId, targetRole: role });

  if ((response as JsonObject)?.status !== 'ok') {
    return false;
  }

  try {
    await firebaseAuth.signOut();
  } catch (err) {
    console.error(err);
    return false;
  }

  window.location.href = `${getAdminBaseUrl()}?assume=1`;

  return true;
};

const getCurrentTokenUser = async (): Promise<AuthUser | null> => {
  let spAccessToken = '',
    spTenantId = '';

  try {
    const response = await get('/auth/status');

    const { accessToken, tenantId, assumedCustomToken } =
      (response as { accessToken: string; tenantId: string; assumedCustomToken: string }) || {};

    if (!tenantId) {
      return null;
    }

    spTenantId = tenantId;

    // role assumption response
    if (assumedCustomToken) {
      spAccessToken = assumedCustomToken;
      // normal login
    } else if (accessToken) {
      spAccessToken = accessToken;
    }
  } catch (err) {
    const axiosError = err as AxiosError;
    // cookie is probably just missing
    if (axiosError.response?.status === 400) {
      return null;
    }
    console.error(err);
    return null;
  }

  if (!spAccessToken || !spTenantId) {
    return null;
  }

  firebaseAuth.tenantId = spTenantId;

  const result = await signInWithCustomToken(firebaseAuth, spAccessToken);

  if (!result?.user) {
    return null;
  }

  return getAuthUser(result.user);
};

const getCurrentFirebaseUser = async (forceRefresh?: boolean): Promise<AuthUser | null> => {
  return new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(firebaseAuth, (user) => {
      if (!user) {
        resolve(null);
        return;
      }

      unsubscribe();

      getAuthUser(user, forceRefresh).then(resolve);
    });
  });
};

const isValidCalimValue = (claimVal: string | null | undefined): boolean =>
  !!claimVal && claimVal !== 'none';

/*
 * Given a Firebase User, get their parsed JWT and then validate that important
 * authentication parameters are present. Return an AuthUser to act as the primary
 * interface for the auth service and the rest of the application, as opposed to directly
 * handling the Firebase User object.
 */
const getAuthUser = async (user: User, forceRefresh = false): Promise<AuthUser | null> => {
  const userWithToken = user as User & { accessToken: string };

  // user missing critical info
  if (!userWithToken.tenantId || !userWithToken.accessToken) {
    console.error('user missing tenant id or access token');
    return null;
  }

  const result = await user.getIdTokenResult(forceRefresh);
  const { claims, token } = result || {};

  if (!claims || !token) {
    console.error('user missing claims or token');
    return null;
  }

  const { partner_id, partner_role, root_role } = claims as {
    partner_id: string;
    partner_role: string;
    root_role: string;
  };

  if (
    !isValidCalimValue(partner_id) ||
    (!isValidCalimValue(root_role) && !isValidCalimValue(partner_role))
  ) {
    console.warn('user missing partner_id/partner_role/root_role');
  }

  return {
    ...user,
    partnerId: partner_id || '',
    role: (partner_role as AuthRole) || null,
    rootRole: (root_role as RootRole) || null,
    token,
  };
};

/*
 * If a user has multiple admin-ui tabs open, then signs out from one of the tabs, this redirect will be
 * triggered by the firebaseAuth.onIdTokenChanged handler defined above. In that case, we need to give
 * the "sign out" tab a chance to sign out of admin-ui, then redirect to auth-ui and sign out. Otherwise,
 * this tab will immediately redirect to auth-ui, use still-valid credentials there to pull a fresh
 * sp-token, and then redirect back to admin-ui and use the sp-token to sign in.
 */
const safeAuthRedirect = debounce(
  () => {
    window.location.href = getAuthRedirectUrl();
  },
  4000,
  { leading: false, trailing: true }
);

/*
 * Attempt to force-refresh the firebase id token. Handle cases of invalid credentials, or missing current user by
 * redirecting back to auth-ui
 */
const refreshTokenUser = () => {
  if (!firebaseAuth.currentUser) {
    safeAuthRedirect();
    return;
  }

  firebaseAuth.currentUser.getIdToken(true).catch(handleTokenRefreshError);
};

/*
 * This function is used by the TanStack Query QueryClient to attempt to refresh credentials, and redirect to
 * the auth site, when a query fails and returns a 401. It is debounced to prevent multiple concurrent or consecutive
 * fetch calls from triggering overlapping refresh requests.
 */
export const safeRefreshTokenUser = debounce(refreshTokenUser, 2000, {
  leading: true,
  trailing: false,
});

const handleTokenRefreshError = (err: unknown) => {
  const { code } = err as FirebaseError;
  if (
    ['auth/id-token-revoked', 'auth/user-token-expired', 'auth/id-token-expired'].includes(code)
  ) {
    safeAuthRedirect();
  }

  // @todo any other codes we want to handle?
  // https://firebase.google.com/docs/auth/admin/errors
};
